/**
 * Sistema de Cache Inteligente para o Frontend
 * Gerencia cache de dados com invalidação automática e estratégias inteligentes
 */

import { CACHE_CONFIG, CACHE_TAGS } from "../shared/constants";
import { getActiveContextForApi } from "../shared/activeContextHolder";
import { logger } from "./logger";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live em ms
  key: string;
  tags: string[]; // Tags para invalidação seletiva
  version: number;
}

interface CacheConfig {
  defaultTTL: number;
  maxSize: number;
  cleanupInterval: number;
  enableCompression: boolean;
}

interface CacheStats {
  hits: number;
  misses: number;
  size: number;
  hitRate: number;
  lastCleanup: Date;
}

class SmartCache {
  private cache = new Map<string, CacheEntry<any>>();
  private inFlight = new Map<string, Promise<any>>();
  private stats: CacheStats = {
    hits: 0,
    misses: 0,
    size: 0,
    hitRate: 0,
    lastCleanup: new Date(),
  };
  private config: CacheConfig;
  private cleanupTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = {
      defaultTTL: CACHE_CONFIG.DEFAULT_TTL,
      maxSize: CACHE_CONFIG.MAX_SIZE,
      cleanupInterval: CACHE_CONFIG.CLEANUP_INTERVAL,
      enableCompression: false,
      ...config,
    };

    this.startCleanupTimer();
    this.setupMemoryPressureHandler();
  }

  /**
   * Obter dados do cache ou executar fetcher se não existir
   */
  async get<T>(
    key: string,
    fetcher: () => Promise<T>,
    options: {
      ttl?: number;
      tags?: string[];
      forceRefresh?: boolean;
    } = {},
  ): Promise<T> {
    const {
      ttl = this.config.defaultTTL,
      tags = [],
      forceRefresh = false,
    } = options;

    // Verificar se existe no cache e não está expirado
    if (!forceRefresh) {
      const cached = this.cache.get(key);
      if (cached && !this.isExpired(cached)) {
        this.stats.hits++;
        this.updateHitRate();
        return cached.data;
      }
    }

    // Já existe uma requisição em voo para esta key: reaproveitar a mesma
    // Promise em vez de disparar um fetch duplicado (evita rajadas de
    // requisições idênticas quando múltiplos componentes/efeitos pedem a
    // mesma key quase simultaneamente, antes do cache ser preenchido).
    const pending = this.inFlight.get(key);
    if (pending) {
      return pending;
    }

    // Cache miss - executar fetcher
    this.stats.misses++;
    this.updateHitRate();

    const fetchPromise = (async () => {
      try {
        const data = await fetcher();

        // Armazenar no cache
        this.set(key, data, { ttl, tags });

        return data;
      } catch (error) {
        // TODO: Usar logger estruturado quando disponível
        logger.error("Cache fetcher error", error as Error, { key });
        throw error;
      } finally {
        this.inFlight.delete(key);
      }
    })();

    this.inFlight.set(key, fetchPromise);
    return fetchPromise;
  }

  /**
   * Armazenar dados no cache
   */
  set<T>(
    key: string,
    data: T,
    options: {
      ttl?: number;
      tags?: string[];
      version?: number;
    } = {},
  ): void {
    const { ttl = this.config.defaultTTL, tags = [], version = 1 } = options;

    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      ttl,
      key,
      tags,
      version,
    };

    this.cache.set(key, entry);
    this.stats.size = this.cache.size;

    // Verificar limite de tamanho
    if (this.cache.size > this.config.maxSize) {
      this.evictOldest();
    }
  }

  /**
   * Invalidar entradas por tags
   */
  invalidateByTags(tags: string[]): number {
    let invalidated = 0;

    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (tags.some((tag) => entry.tags.includes(tag))) {
        this.cache.delete(key);
        invalidated++;
      }
    }

    this.stats.size = this.cache.size;
    return invalidated;
  }

  /**
   * Invalidar entrada específica
   */
  invalidate(key: string): boolean {
    const deleted = this.cache.delete(key);
    this.stats.size = this.cache.size;
    return deleted;
  }

  /**
   * Limpar todo o cache
   */
  clear(): void {
    this.cache.clear();
    this.stats.size = 0;
  }

  /**
   * Obter estatísticas do cache
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Verificar se entrada está expirada
   */
  private isExpired(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  /**
   * Atualizar taxa de hit
   */
  private updateHitRate(): void {
    const total = this.stats.hits + this.stats.misses;
    this.stats.hitRate = total > 0 ? (this.stats.hits / total) * 100 : 0;
  }

  /**
   * Remover entrada mais antiga
   */
  private evictOldest(): void {
    let oldestKey = "";
    let oldestTime = Date.now();

    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Iniciar timer de limpeza automática
   */
  private startCleanupTimer(): void {
    this.cleanupTimer = setInterval(() => {
      this.cleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Limpeza automática de entradas expiradas
   */
  private cleanup(): void {
    const initialSize = this.cache.size;
    let expiredCount = 0;

    // Remove entradas expiradas
    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (this.isExpired(entry)) {
        this.cache.delete(key);
        expiredCount++;
      }
    }

    // Se ainda está acima do limite após remover expiradas, remove entradas antigas (LRU básico)
    if (this.cache.size > this.config.maxSize) {
      const entriesToRemove = this.cache.size - this.config.maxSize;
      const sortedEntries = Array.from(this.cache.entries()).sort(
        ([, a], [, b]) => a.timestamp - b.timestamp,
      ); // Ordena por timestamp (mais antigo primeiro)

      for (let i = 0; i < entriesToRemove; i++) {
        this.cache.delete(sortedEntries[i][0]);
        expiredCount++;
      }
    }

    this.stats.size = this.cache.size;
    this.stats.lastCleanup = new Date();

    if (expiredCount > 0) {
      logger.info("Cache cleanup completed", {
        removed: expiredCount,
        before: initialSize,
        after: this.cache.size,
      });
    }
  }

  /**
   * Handler para pressão de memória do navegador
   */
  private setupMemoryPressureHandler(): void {
    // Verificação robusta da API de performance.memory
    if ("memory" in performance && performance.memory) {
      const memInfo = performance.memory as any;

      // Verifica se tem as propriedades esperadas
      if (
        typeof memInfo.usedJSHeapSize === "number" &&
        typeof memInfo.totalJSHeapSize === "number" &&
        memInfo.totalJSHeapSize > 0
      ) {
        // Monitora uso de memória e limpa cache se necessário
        const checkMemoryUsage = () => {
          try {
            const usageRatio = memInfo.usedJSHeapSize / memInfo.totalJSHeapSize;

            if (usageRatio > 0.8) {
              // 80% de uso
              logger.warn("High memory pressure detected, clearing cache", {
                usageRatio: usageRatio.toFixed(2),
                used: memInfo.usedJSHeapSize,
                total: memInfo.totalJSHeapSize,
              });
              this.clear();
            }
          } catch (error) {
            logger.warn("Error checking memory usage", error as Error);
          }
        };

        // Verifica uso de memória a cada 30 segundos
        setInterval(checkMemoryUsage, 30000);
      } else {
        logger.debug(
          "Performance.memory API available but missing expected properties",
        );
      }
    }
  }

  /**
   * Limpa cache por tags
   */
  clearByTags(tags: string[]): void {
    let removedCount = 0;

    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (tags.some((tag) => entry.tags.includes(tag))) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    if (removedCount > 0) {
      this.stats.size = this.cache.size;
      logger.info("Cache entries cleared by tags", {
        removed: removedCount,
        tags,
      });
    }
  }

  /**
   * Obtém estatísticas detalhadas do cache
   */
  getDetailedStats(): CacheStats & {
    entriesByTag: Record<string, number>;
    averageTTL: number;
    memoryUsage?: { used: number; total: number; ratio: number };
  } {
    const tagCounts: Record<string, number> = {};
    let ttlSum = 0;

    for (const entry of Array.from(this.cache.values())) {
      entry.tags.forEach((tag) => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
      ttlSum += entry.ttl;
    }

    const result: any = {
      ...this.stats,
      entriesByTag: tagCounts,
      averageTTL: this.cache.size > 0 ? ttlSum / this.cache.size : 0,
    };

    // Adiciona informações de memória se disponível
    if ("memory" in performance) {
      const memInfo = (performance as any).memory;
      if (
        memInfo &&
        typeof memInfo.usedJSHeapSize === "number" &&
        typeof memInfo.totalJSHeapSize === "number"
      ) {
        result.memoryUsage = {
          used: memInfo.usedJSHeapSize,
          total: memInfo.totalJSHeapSize,
          ratio: memInfo.usedJSHeapSize / memInfo.totalJSHeapSize,
        };
      }
    }

    return result;
  }

  /**
   * Destruir instância e limpar recursos
   */
  destroy(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
    this.clear();
  }
}

// Instância global do cache
const globalCache = new SmartCache({
  defaultTTL: CACHE_CONFIG.DEFAULT_TTL,
  maxSize: CACHE_CONFIG.MAX_SIZE,
  cleanupInterval: CACHE_CONFIG.CLEANUP_INTERVAL,
});

// Hook personalizado para usar o cache
export function useSmartCache() {
  return {
    get: globalCache.get.bind(globalCache),
    set: globalCache.set.bind(globalCache),
    invalidate: globalCache.invalidate.bind(globalCache),
    invalidateByTags: globalCache.invalidateByTags.bind(globalCache),
    clear: globalCache.clear.bind(globalCache),
    getStats: globalCache.getStats.bind(globalCache),
  };
}

// Utilitários para cache de posts (uso de hook em classe: instâncias usadas em contexto React)
export class PostsCache {
  private cache = useSmartCache(); // eslint-disable-line react-hooks/rules-of-hooks

  // Identifica a organização/empresa cliente ativa para evitar que o cache
  // (singleton global, sem escopo por request) sirva dados de um tenant
  // para outro ao trocar de contexto (ex: admin alternando entre empresas).
  private contextKeyPart(): string {
    const ctx = getActiveContextForApi();
    if (!ctx) return "ctx:none";
    return ctx.type === "CLIENT_COMPANY"
      ? `ctx:cc${ctx.clientCompanyId ?? "?"}`
      : `ctx:org${ctx.organizationId ?? "?"}`;
  }

  async getPendingPosts(
    fetcher: () => Promise<{ posts: any[]; totalCount: number }>,
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
  ) {
    const key = `posts:${this.contextKeyPart()}:pending:p${page}:l${limit}`;
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.POSTS_TTL,
      tags: [CACHE_TAGS.POSTS, "pending"],
      forceRefresh,
    });
  }

  async getApprovedPosts(
    fetcher: () => Promise<{ posts: any[]; totalCount: number }>,
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
  ) {
    const key = `posts:${this.contextKeyPart()}:approved:p${page}:l${limit}`;
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.POSTS_APPROVED_TTL,
      tags: [CACHE_TAGS.POSTS, "approved"],
      forceRefresh,
    });
  }

  async getPublishedPosts(
    fetcher: () => Promise<{ posts: any[]; totalCount: number }>,
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
  ) {
    const key = `posts:${this.contextKeyPart()}:published:p${page}:l${limit}`;
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.POSTS_PUBLISHED_TTL,
      tags: [CACHE_TAGS.POSTS, "published"],
      forceRefresh,
    });
  }

  async getRejectedPosts(
    fetcher: () => Promise<{ posts: any[]; totalCount: number }>,
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
  ) {
    const key = `posts:${this.contextKeyPart()}:rejected:p${page}:l${limit}`;
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.POSTS_TTL,
      tags: [CACHE_TAGS.POSTS, "rejected"],
      forceRefresh,
    });
  }

  async getScheduledPosts(
    fetcher: () => Promise<{ posts: any[]; totalCount: number }>,
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
  ) {
    const key = `posts:${this.contextKeyPart()}:scheduled:p${page}:l${limit}`;
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.POSTS_TTL,
      tags: [CACHE_TAGS.POSTS, "scheduled"],
      forceRefresh,
    });
  }

  async getAllPosts(
    fetcher: () => Promise<{ posts: any[]; totalCount: number }>,
    forceRefresh: boolean = false,
    page: number = 1,
    limit: number = 10,
  ) {
    const key = `posts:${this.contextKeyPart()}:all:p${page}:l${limit}`;
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.POSTS_TTL,
      tags: [CACHE_TAGS.POSTS, "all"],
      forceRefresh,
    });
  }

  async getStats(fetcher: () => Promise<any>) {
    return this.cache.get(`posts:${this.contextKeyPart()}:stats`, fetcher, {
      ttl: CACHE_CONFIG.POSTS_STATS_TTL,
      tags: [CACHE_TAGS.POSTS, "stats"],
    });
  }

  invalidateAllPosts() {
    return this.cache.invalidateByTags([CACHE_TAGS.POSTS]);
  }

  invalidatePostById(_postId: number) {
    // Invalidar todas as listas que podem conter este post
    this.cache.invalidateByTags([CACHE_TAGS.POSTS]);
  }
}

// Utilitários para cache de analytics (uso de hook em classe: instâncias usadas em contexto React)
export class AnalyticsCache {
  private cache = useSmartCache(); // eslint-disable-line react-hooks/rules-of-hooks

  async getPerformanceDashboard(
    fetcher: () => Promise<any>,
    key: string = "analytics:dashboard",
  ) {
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.ANALYTICS_DASHBOARD_TTL,
      tags: [CACHE_TAGS.ANALYTICS, "dashboard"],
    });
  }

  async getPredictions(postData: any, fetcher: () => Promise<any>) {
    const key = `analytics:predictions:${JSON.stringify(postData)}`;
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.ANALYTICS_PREDICTIONS_TTL,
      tags: [CACHE_TAGS.ANALYTICS, "predictions"],
    });
  }

  async getInsights(
    fetcher: () => Promise<any>,
    key: string = "analytics:insights",
  ) {
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.ANALYTICS_INSIGHTS_TTL,
      tags: [CACHE_TAGS.ANALYTICS, "insights"],
    });
  }

  async getTrends(
    fetcher: () => Promise<any>,
    key: string = "analytics:trends",
  ) {
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.ANALYTICS_INSIGHTS_TTL,
      tags: [CACHE_TAGS.ANALYTICS, "trends"],
    });
  }

  async getSentiment(
    fetcher: () => Promise<any>,
    key: string = "analytics:sentiment",
  ) {
    return this.cache.get(key, fetcher, {
      ttl: CACHE_CONFIG.ANALYTICS_INSIGHTS_TTL,
      tags: [CACHE_TAGS.ANALYTICS, "sentiment"],
    });
  }

  invalidateAllAnalytics() {
    return this.cache.invalidateByTags([CACHE_TAGS.ANALYTICS]);
  }
}

// Instâncias globais
export const postsCache = new PostsCache();
export const analyticsCache = new AnalyticsCache();

// Exportar instância global do cache
export default globalCache;
